import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCheck, faLock } from '@fortawesome/pro-light-svg-icons';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import type {
	ClearIndicatorProps,
	GroupBase,
	InputProps,
	MenuListProps,
	MultiValue,
	MultiValueProps,
	OptionProps,
	StylesConfig,
	ValueContainerProps,
} from 'react-select';
import Select, { components } from 'react-select';
import { useTheme, useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import { formTheme } from '../../services/ThemeService';
import { cn } from '../../utils/cn';

export interface MultiSelectOption {
	value: string | number;
	label: string;
	isFixed?: boolean;
	count?: number;
	_count?: number; // Internal count property to avoid react-select filtering issues
	subtitle?: string;
	/** Extra small-print lines rendered under the subtitle in the menu */
	detailLines?: Array<string>;
	badge?: string;
	/** Optional leading icon shown before the label in both the menu option
	 *  and the selected chip (e.g. to distinguish people / teams / roles). */
	leadingIcon?: IconDefinition;
	/**
	 * PD-15560: heading this option sits under in the menu (e.g. "Warehouses" /
	 * "Client Locations" on the merchandiser Placement filter). A heading is
	 * rendered above the first rendered option of each run, so the caller must
	 * supply the options already ordered by group. Options without a group
	 * label render exactly as before.
	 */
	groupLabel?: string;
}

/**
 * PD-15560: insert a heading above the first option of each group run. Driven
 * by the RENDERED children rather than the `options` prop so it stays correct
 * when react-select filters the list or hides already-selected options —
 * anchoring a heading to an option that isn't rendered would drop the heading.
 */
const withGroupHeadings = (
	children: React.ReactNode,
	headingClassName: string,
): React.ReactNode => {
	const rendered = React.Children.toArray(children);
	let lastGroup: string | undefined;
	let sawGroup = false;
	const result: Array<React.ReactNode> = [];

	for (const child of rendered) {
		const data = React.isValidElement<{ data?: MultiSelectOption }>(child)
			? child.props.data
			: undefined;

		// Non-option children (the "no options" message, the select-all header)
		// carry no data — pass them through without disturbing the run.
		if (data == null) {
			result.push(child);
			continue;
		}

		if (data.groupLabel != null && data.groupLabel !== lastGroup) {
			sawGroup = true;
			result.push(
				<div
					key={`group-heading-${data.groupLabel}`}
					className={headingClassName}
				>
					{data.groupLabel}
				</div>,
			);
		}
		lastGroup = data.groupLabel;
		result.push(child);
	}

	return sawGroup ? result : children;
};

const GROUP_HEADING_CLASS =
	'px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide';

const GroupedMenuList = (
	props: MenuListProps<MultiSelectOption, true, GroupBase<MultiSelectOption>>,
): React.ReactElement => {
	const colors = useThemeColors();

	return (
		<components.MenuList {...props}>
			{withGroupHeadings(
				props.children,
				cn(GROUP_HEADING_CLASS, colors.classes.textSecondary),
			)}
		</components.MenuList>
	);
};

interface MultiSelectProps {
	/**
	 * Keep the control to a single line, letting overflow clip rather than
	 * wrap. The filter bar is a row of equal-height controls; a preset with
	 * several values would otherwise wrap to a second line and stand 30px
	 * taller than everything beside it.
	 */
	singleLine?: boolean;
	name: string;
	value?: MultiSelectOption[];
	options: MultiSelectOption[];
	onChange: (selected: MultiSelectOption[]) => void;
	placeholder?: string;
	isDisabled?: boolean;
	isLoading?: boolean;
	isClearable?: boolean;
	isSearchable?: boolean;
	className?: string;
	menuPlacement?: 'auto' | 'bottom' | 'top';
	maxMenuHeight?: number;
	onInputChange?: (value: string) => void;
	onMenuOpen?: () => void;
	onMenuClose?: () => void;
	menuIsOpen?: boolean;
	size?: 'xs' | 'sm' | 'md' | 'lg';
	filterOption?: ((option: any, inputValue: string) => boolean) | null;
	summaryMode?: boolean;
	summaryLabel?: string;
	/**
	 * PD-14078: adds "Select all" / "Clear" shortcuts pinned above the
	 * option list — multi-selects with large collections (e.g. a chain's
	 * child accounts) need a one-click way to include everything.
	 */
	enableSelectAll?: boolean;
	/**
	 * PD-13733: keep the typed search term after the user ticks or unticks an
	 * option instead of clearing it. A long option list (the client activity-type
	 * filter offers 57) is worked by searching for a word and toggling several
	 * matches; clearing on the first click threw the menu back to the whole
	 * list, so the remaining matches could not be reached without retyping.
	 * Opt-in because the callers that pair `onInputChange` with a server-side
	 * search depend on the term clearing once a value is set. The term is still
	 * cleared when the menu closes, so the next open starts fresh.
	 */
	preserveSearchOnSelect?: boolean;
	hideSelectedOptions?: boolean;
	menuMinWidth?: number;
}

// Custom option component to show lock icon for fixed options and count badges
const Option = (props: OptionProps<MultiSelectOption>) => {
	const colors = useThemeColors();
	return (
		<components.Option {...props}>
			<div
				className="flex items-center justify-between w-full"
				style={{ fontSize: 'inherit' }}
			>
				<div
					className="flex items-center gap-2"
					style={{ fontSize: 'inherit' }}
				>
					<div className="w-4 flex-shrink-0">
						{props.isSelected && (
							<FontAwesomeIcon
								icon={faCheck}
								className="text-xs"
								style={{ color: colors.values.primaryDark }}
							/>
						)}
					</div>
					{props.data.leadingIcon != null && (
						<FontAwesomeIcon
							icon={props.data.leadingIcon}
							className="text-xs flex-shrink-0"
							style={{ color: colors.values.textSecondary }}
						/>
					)}
					<div className="flex flex-col" style={{ fontSize: 'inherit' }}>
						<span style={{ fontSize: 'inherit' }}>{props.data.label}</span>
						{props.data.subtitle != null && (
							<span className={cn('text-xs', colors.classes.textSecondary)}>
								{props.data.subtitle}
							</span>
						)}
						{props.data.detailLines?.map((line) => (
							<span
								key={line}
								className={cn('text-xs', colors.classes.textSecondary)}
							>
								{line}
							</span>
						))}
					</div>
				</div>
				<div className="flex items-center gap-2">
					{props.data.badge != null && (
						<span
							className={cn(
								'px-1.5 py-0.5 text-[10px] font-medium rounded',
								colors.classes.bgSecondary,
								colors.classes.textSecondary,
							)}
						>
							{props.data.badge}
						</span>
					)}
					{(props.data.count != null || props.data._count != null) && (
						<span
							className={cn(
								'px-2 py-0.5 text-xs font-medium rounded-full',
								colors.classes.bgSecondary,
								colors.classes.textPrimary,
							)}
						>
							{props.data.count ?? props.data._count}
						</span>
					)}
					{props.data.isFixed === true && (
						<FontAwesomeIcon
							icon={faLock}
							className={cn(colors.classes.textSecondary, 'text-xs')}
						/>
					)}
				</div>
			</div>
		</components.Option>
	);
};

// Custom MultiValue component to show lock icon for fixed options
const CustomMultiValue = (props: MultiValueProps<MultiSelectOption>) => {
	const { colorScheme } = useTheme();
	const formColors = formTheme.values[colorScheme];
	return (
		<components.MultiValue {...props}>
			<div className="flex items-center gap-1.5">
				{props.data.leadingIcon != null && (
					<FontAwesomeIcon
						icon={props.data.leadingIcon}
						className="text-[10px]"
						style={{ color: formColors.text }}
					/>
				)}
				<span>{props.children}</span>
				{props.data.isFixed && (
					<FontAwesomeIcon
						icon={faLock}
						className="text-xs mr-1"
						style={{ color: formColors.text }}
					/>
				)}
			</div>
		</components.MultiValue>
	);
};

// Custom ClearIndicator — uses the same FontAwesome icon and the same
// Tailwind class pattern as BaseTextInput so the X visual matches across
// every filter input. We deliberately use className (not inline color)
// because that's what BaseTextInput does, and it produces the same
// rendered color in both light and dark modes.
const CustomClearIndicator = (
	props: ClearIndicatorProps<MultiSelectOption, true>,
): React.ReactElement => {
	const colors = useThemeColors();

	return (
		<components.ClearIndicator {...props}>
			<FontAwesomeIcon
				icon={faTimes}
				className={cn(
					colors.classes.textSecondary,
					'hover:' + colors.classes.textPrimary,
					'transition-colors cursor-pointer',
				)}
				style={{ fontSize: '14px' }}
			/>
		</components.ClearIndicator>
	);
};

const CustomInput = (props: InputProps<MultiSelectOption, true>) => (
	<components.Input
		{...props}
		data-bwignore="true"
		data-lpignore="true"
		data-1p-ignore="true"
		autoComplete="off"
	/>
);

// Removed global customStyles - will be defined inside component

export function MultiSelect({
	name,
	value = [],
	options,
	onChange,
	placeholder = 'Select...',
	isDisabled = false,
	isLoading = false,
	isClearable = true,
	isSearchable = true,
	className,
	menuPlacement = 'auto',
	maxMenuHeight = 300,
	onInputChange,
	onMenuOpen,
	onMenuClose,
	menuIsOpen: controlledMenuIsOpen,
	size = 'md',
	singleLine = false,
	filterOption,
	summaryMode = false,
	summaryLabel = 'selected',
	enableSelectAll = false,
	preserveSearchOnSelect = false,
	hideSelectedOptions = true,
	menuMinWidth,
}: MultiSelectProps) {
	const colors = useThemeColors();
	const { colorScheme } = useTheme();
	const { t } = useTranslation();
	const formColors = formTheme.values[colorScheme];
	const [menuIsOpen, setMenuIsOpen] = React.useState(false);
	const [inputValue, setInputValue] = React.useState('');
	const instanceId = `${name}-multiselect`;

	// PD-15716 — close an ORPHANED open menu on the next interaction anywhere
	// else on the page. react-select closes its menu from the input's blur, and
	// with closeMenuOnSelect=false the menu deliberately stays open across
	// picks. When a pick makes the host re-render (a list-builder filter bar
	// refetches the list on every change) the input loses focus without a
	// blur reaching react-select, so the menu stays open with nothing focused
	// — and nothing can ever close it except a click that happens to land on
	// the control. A row action that opens a modal (its handler stops
	// propagation) leaves the option list rendered over the modal until the
	// user clicks out (customer report, Current Licenses → Confirm Matches).
	// Listen in the capture phase — a stopPropagation on the bubbling click
	// cannot reach it — and scope to THIS instance via react-select's own ids,
	// so picking inside our menu or on our control is still ours. Only the
	// uncontrolled menu is handled; a parent that owns menuIsOpen owns closing.
	React.useEffect(() => {
		if (controlledMenuIsOpen !== undefined || !menuIsOpen) {
			return undefined;
		}
		const isOurs = (target: EventTarget | null): boolean => {
			if (!(target instanceof Element)) {
				return false;
			}
			if (target.closest(`#react-select-${instanceId}-listbox`) != null) {
				return true;
			}
			const control = target.closest('.react-select__control');
			return (
				control?.querySelector(`#react-select-${instanceId}-input`) != null
			);
		};
		const closeIfOutside = (event: Event): void => {
			if (isOurs(event.target)) {
				return;
			}
			setMenuIsOpen(false);
			setInputValue('');
			onMenuClose?.();
		};
		document.addEventListener('pointerdown', closeIfOutside, true);
		document.addEventListener('focusin', closeIfOutside, true);
		return (): void => {
			document.removeEventListener('pointerdown', closeIfOutside, true);
			document.removeEventListener('focusin', closeIfOutside, true);
		};
	}, [menuIsOpen, controlledMenuIsOpen, instanceId, onMenuClose]);

	// In summary mode the control renders no value chips, so the menu is the
	// ONLY place selections are visible. Hiding selected options there would
	// collapse a fully-selected list to "No options" with no way to review or
	// untick individual picks — keep them listed (the Option renderer shows a
	// checkmark) regardless of the hideSelectedOptions prop.
	const effectiveHideSelectedOptions = summaryMode
		? false
		: hideSelectedOptions;

	// Size configurations matching Input component
	const sizeConfig = {
		xs: {
			// The filter bar's control height — see --control-h-xs in design-scale.
			// The old 22px minimum never governed: the value container's padding
			// plus a value chip pushed the control to 40px, so the multi-select
			// stood 8px taller than the selects sharing the bar.
			minHeight: 'var(--control-h-xs, 2rem)',
			fontSize: '0.75rem', // text-xs
			padding: '2px 6px',
			horizontalPadding: '10px', // Placeholder position - matches Select's px-2.5
			multiValueFontSize: '0.75rem', // text-xs
			controlPadding: '0.125rem 0.375rem', // py-0.5 px-1.5
			optionPadding: '6px 10px', // Match Select: py-1.5 px-2.5
			lineHeight: '1.25rem', // leading-5
			// A 20px icon in 6px of padding makes a 32px indicator column, which
			// on its own would hold the control at 34px and defeat the bar height.
			indicatorPadding: '4px',
		},
		sm: {
			minHeight: '30px',
			fontSize: '0.875rem', // text-sm
			padding: '3px 12px',
			horizontalPadding: '12px',
			multiValueFontSize: '0.75rem', // text-xs
			controlPadding: '0.375rem 0.625rem', // py-1.5 px-2.5
			optionPadding: '6px 12px',
			lineHeight: '1.25rem', // leading-5
			indicatorPadding: '6px',
		},
		md: {
			// The one control height for the tier — see --control-h in design-scale.
			minHeight: 'var(--control-h, 2.25rem)',
			fontSize: '0.875rem', // text-sm
			padding: '4px 12px',
			horizontalPadding: '12px',
			multiValueFontSize: '0.625rem', // 10px - even smaller
			controlPadding: '0.5rem 0.75rem', // py-2 px-3
			optionPadding: '8px 12px',
			lineHeight: '1.25rem', // leading-5
			indicatorPadding: '6px',
		},
		lg: {
			minHeight: '42px',
			fontSize: '1rem', // text-base
			padding: '4px 16px',
			horizontalPadding: '16px',
			multiValueFontSize: '0.875rem', // text-sm
			controlPadding: '0.625rem 1rem', // py-2.5 px-4
			optionPadding: '10px 16px',
			lineHeight: '1.5rem', // leading-6
			indicatorPadding: '6px',
		},
	};

	const currentSize = sizeConfig[size] || sizeConfig.md;

	// Theme-aware styles for react-select
	const customStyles: StylesConfig<MultiSelectOption, true> = {
		control: (base, state) => ({
			...base,
			// react-select measures minHeight against the CONTENT box, so its
			// 1px border would otherwise make every select 2px taller than the
			// inputs and buttons sharing the row.
			boxSizing: 'border-box' as const,
			minHeight: currentSize.minHeight,
			fontSize: currentSize.fontSize,
			lineHeight: currentSize.lineHeight,
			backgroundColor: state.isDisabled
				? formColors.backgroundDisabled
				: formColors.background,
			borderColor: state.isDisabled
				? formColors.borderDisabled
				: state.isFocused
					? formColors.borderFocus
					: formColors.border,
			borderRadius: '0.375rem',
			borderWidth: '1px',
			boxShadow: 'none', // Removed focus ring to eliminate the lighter border highlight
			'&:hover': {
				borderColor: state.isFocused
					? formColors.borderFocus
					: formColors.border,
			},
			cursor: state.isDisabled ? 'not-allowed' : 'default',
		}),
		/*
		 * react-select's default indicator padding (8px around a 20px icon)
		 * makes the indicator column 36px tall, which pushes the control past
		 * the --control-h minimum and leaves every select 2px taller than the
		 * inputs and buttons on the same row. Trimmed so the token governs.
		 */
		dropdownIndicator: (base) => ({
			...base,
			padding: currentSize.indicatorPadding,
		}),
		valueContainer: (base) => ({
			...base,
			padding: currentSize.padding,
			gap: '4px',
			display: 'flex',
			flexWrap: singleLine ? ('nowrap' as const) : ('wrap' as const),
			...(singleLine ? { overflow: 'hidden' as const } : {}),
			position: 'relative',
		}),
		multiValue: (base, state) => ({
			...base,
			backgroundColor: formColors.tagBackground, // Same for both fixed and regular
			borderRadius: '0.25rem',
			margin: '1px 2px',
			padding: '0px',
		}),
		multiValueLabel: (base, state) => ({
			...base,
			color: formColors.tagText, // Same for both fixed and regular
			paddingLeft: '4px',
			paddingRight: '2px',
			paddingTop: '2px',
			paddingBottom: '2px',
			fontSize: currentSize.multiValueFontSize,
		}),
		multiValueRemove: (base, state) => ({
			...base,
			display: state.data.isFixed ? 'none' : 'flex',
			color: formColors.text,
			':hover': {
				backgroundColor: formColors.backgroundHover,
				color: colors.values.danger,
			},
		}),
		clearIndicator: (base) => ({
			...base,
			padding: '0 4px',
			cursor: 'pointer',
		}),
		menu: (base) => ({
			...base,
			backgroundColor: formColors.menuBackground,
			borderRadius: '0.375rem',
			boxShadow:
				'0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
			marginTop: '4px',
			border: `1px solid ${formColors.border}`,
			zIndex: 9999,
			overflow: 'hidden',
			...(menuMinWidth != null ? { minWidth: `${menuMinWidth}px` } : {}),
		}),
		menuList: (base) => ({
			...base,
			padding: '4px 0',
			borderRadius: '0.375rem',
		}),
		menuPortal: (base) => ({
			...base,
			zIndex: 9999,
		}),
		option: (base, state) => ({
			...base,
			backgroundColor: state.isSelected
				? formColors.optionSelected
				: state.isFocused
					? formColors.optionHover
					: 'transparent',
			color: state.isSelected ? formColors.optionSelectedText : formColors.text,
			padding: currentSize.optionPadding,
			fontSize: currentSize.fontSize,
			cursor: 'pointer',
			':active': {
				backgroundColor: formColors.optionHover,
			},
		}),
		placeholder: (base) => ({
			...base,
			color: formColors.textPlaceholder,
			marginLeft: 0,
			marginRight: 0,
			position: 'absolute',
			left: currentSize.horizontalPadding,
			top: '50%',
			transform: 'translateY(-50%)',
			pointerEvents: 'none',
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			maxWidth: `calc(100% - ${currentSize.horizontalPadding} - ${currentSize.horizontalPadding})`,
		}),
		input: (base) => ({
			...base,
			color: formColors.text,
			lineHeight: currentSize.lineHeight,
			margin: 0,
			padding: 0,
			width: 'auto',
			gridArea: '1 / 2',
			outline: 'none !important',
			border: 'none',
			boxShadow: 'none',
			caretColor: formColors.text,
			'&:focus': {
				outline: 'none !important',
				boxShadow: 'none',
			},
		}),
		singleValue: (base) => ({
			...base,
			marginLeft: 0,
			marginRight: 0,
		}),
		loadingIndicator: (base) => ({
			...base,
			color: formColors.text,
		}),
		noOptionsMessage: (base) => ({
			...base,
			color: formColors.textPlaceholder,
		}),
	};

	// Handle change to prevent removal of fixed options
	const handleChange = (newValue: MultiValue<MultiSelectOption>) => {
		const selectedOptions = newValue as MultiSelectOption[];

		// Ensure fixed options remain selected
		const fixedOptions = value.filter((option) => option.isFixed);
		const nonFixedSelected = selectedOptions.filter(
			(option) => !option.isFixed,
		);
		const finalSelection = [...fixedOptions, ...nonFixedSelected];

		// Remove duplicates based on value
		const uniqueSelection = finalSelection.filter(
			(option, index, self) =>
				index === self.findIndex((o) => o.value === option.value),
		);

		// Clear the search input after selection unless the caller asked to keep
		// it, so the user can carry on toggling the matches they searched for.
		if (!preserveSearchOnSelect) {
			setInputValue('');
		}

		onChange(uniqueSelection);
	};

	// Custom ValueContainer for summary mode - shows count instead of individual tags.
	// PD-13733: memoized so it keeps a STABLE component identity across renders.
	// When this was defined inline in the render body, react-select saw a new
	// component type every keystroke and remounted the value container's <input>,
	// dropping focus + the typed text after a single character (so search never
	// filtered). A stable identity lets typing accumulate and react-select's
	// default filter then narrows the list.
	const SummaryValueContainer = React.useMemo(
		() =>
			(
				props: ValueContainerProps<MultiSelectOption, true>,
			): React.ReactElement => {
				const selectedCount = props.getValue().length;
				const totalCount = options.length;

				let summaryText: string;

				if (selectedCount === 0) {
					summaryText = placeholder;
				} else if (selectedCount === totalCount) {
					summaryText = `${t('All')} ${t(summaryLabel)}`;
				} else {
					summaryText = `${selectedCount} ${t(summaryLabel)}`;
				}

				return (
					<components.ValueContainer {...props}>
						<span
							style={{
								color:
									selectedCount === 0
										? formColors.textPlaceholder
										: formColors.text,
							}}
						>
							{summaryText}
						</span>
						{React.Children.toArray(props.children).filter(
							(child) =>
								React.isValidElement(child) &&
								(child.type === components.Input || child.type === CustomInput),
						)}
					</components.ValueContainer>
				);
			},
		[options, placeholder, formColors, t, summaryLabel],
	);

	// Select all / Clear header row pinned above the scrolling option list.
	// Memoized for the same stable-identity reason as SummaryValueContainer.
	const SelectAllMenuList = React.useMemo(
		() =>
			(
				props: MenuListProps<
					MultiSelectOption,
					true,
					GroupBase<MultiSelectOption>
				>,
			): React.ReactElement => {
				const allSelected =
					value.length === options.length && options.length > 0;
				return (
					<components.MenuList {...props}>
						<div
							className="flex flex-col items-start gap-0.5 px-3 py-1.5 border-b text-xs font-medium sticky top-0 z-10"
							style={{
								borderColor: formColors.border,
								backgroundColor: formColors.background,
							}}
						>
							<button
								type="button"
								className="whitespace-nowrap underline-offset-2 hover:underline"
								style={{ color: colors.values.link }}
								onMouseDown={(event) => {
									// onMouseDown beats react-select's blur handling
									event.preventDefault();
									onChange(allSelected ? [] : [...options]);
								}}
							>
								{allSelected
									? t('Clear all')
									: `${t('Select all')} (${options.length})`}
							</button>
							<span
								className="whitespace-nowrap"
								style={{ color: formColors.textPlaceholder }}
							>
								{`${value.length} ${t('selected')}`}
							</span>
						</div>
						{withGroupHeadings(
							props.children,
							cn(GROUP_HEADING_CLASS, colors.classes.textSecondary),
						)}
					</components.MenuList>
				);
			},
		[value, options, formColors, t, onChange, colors],
	);

	// PD-15560: only swap in the grouping MenuList when some option actually
	// carries a group label, so every existing caller keeps react-select's own
	// MenuList untouched.
	const hasGroupedOptions = options.some((option) => option.groupLabel != null);

	// Build components object based on whether summary mode is enabled
	const selectComponents = summaryMode
		? {
				Option,
				Input: CustomInput,
				ValueContainer: SummaryValueContainer,
				MultiValue: (): null => null,
				ClearIndicator: CustomClearIndicator,
				...(enableSelectAll
					? { MenuList: SelectAllMenuList }
					: hasGroupedOptions
						? { MenuList: GroupedMenuList }
						: {}),
			}
		: {
				Option,
				MultiValue: CustomMultiValue,
				Input: CustomInput,
				ClearIndicator: CustomClearIndicator,
				...(enableSelectAll
					? { MenuList: SelectAllMenuList }
					: hasGroupedOptions
						? { MenuList: GroupedMenuList }
						: {}),
			};

	return (
		<Select<MultiSelectOption, true>
			instanceId={instanceId}
			name={name}
			value={value}
			onChange={handleChange}
			options={options}
			isMulti
			placeholder={placeholder}
			isDisabled={isDisabled}
			isLoading={isLoading}
			isClearable={isClearable && !value.some((v) => v.isFixed)}
			isSearchable={isSearchable}
			className={cn('react-select-container', className)}
			classNamePrefix="react-select"
			styles={customStyles}
			components={selectComponents}
			menuPlacement={menuPlacement}
			maxMenuHeight={maxMenuHeight}
			closeMenuOnSelect={false}
			hideSelectedOptions={effectiveHideSelectedOptions}
			backspaceRemovesValue={false}
			getOptionLabel={(option) => option.label}
			getOptionValue={(option) => String(option.value)}
			inputValue={inputValue}
			onInputChange={(newValue, { action }) => {
				if (action === 'input-change') {
					setInputValue(newValue);
					onInputChange?.(newValue);
				} else if (action === 'menu-close') {
					// Closing the menu always ends the search.
					setInputValue('');
					onInputChange?.('');
				} else if (action === 'set-value' && !preserveSearchOnSelect) {
					// Picking an option clears the search by default; callers that
					// opt in keep the term so the filtered list survives the click.
					setInputValue('');
					onInputChange?.('');
				}

				return inputValue;
			}}
			onMenuOpen={() => {
				setMenuIsOpen(true);
				onMenuOpen?.();
			}}
			onMenuClose={() => {
				setMenuIsOpen(false);
				setInputValue(''); // Clear input when menu closes
				onMenuClose?.();
			}}
			menuIsOpen={
				controlledMenuIsOpen !== undefined ? controlledMenuIsOpen : menuIsOpen
			}
			menuPortalTarget={document.body}
			menuShouldBlockScroll={false}
			filterOption={filterOption}
		/>
	);
}
